// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — this device's connection to a shared session's room
 * (`docs/SHARED_AR_PLAN.md`): one WebSocket, kept open, with the room's
 * messages turned into three facts — who leads, how many are here, and
 * the lead's latest state.
 *
 * It decides nothing about the sphere; `roomSync` does that. What it owns
 * is the socket's life: a dropped connection is retried with a growing
 * pause, because a phone in someone's hand changes networks and sleeps,
 * and a session should survive both. A reconnect is a new seat in the
 * room, so a lead that drops comes back as a follower; that is the room's
 * rule (the longest-connected leads), not something to work around here.
 *
 * AYNI (owner, 2026-10-12): the room knows a reconnection as the same
 * person, keeps their number, name and answers, and refuses someone it
 * removed. AYNI (security fix PR 4, S-10, 2026-10-21): who a browser is
 * is the site's to say, not the page's. Before each connection to the
 * site's own route the page calls `GET /api/room/:code/ticket`
 * (`claimRoomTicket`), which sets a cookie the page cannot read, and the
 * door derives the person key from that cookie; nothing is sent in the
 * address any more. Only a development server pointed at the rooms Worker
 * itself (`VITE_ROOM_WS_URL`), which has no door, still sends a key of
 * its own (`personKeyFor`, `?pk=`). Removed, the page is told so and does
 * not reconnect.
 *
 * `send` is a no-op unless this device is the lead. The room would drop
 * the message anyway; not sending it saves the phone the radio.
 * `sendMessage` carries the activity messages (`roomActivity.ts`), which
 * anyone may send; the room decides which it accepts from whom.
 */

import {
  normalizeRoomCode,
  parseServerMessage,
  type RoomClientMessage,
  type RoomSeat,
  type RoomServerMessage,
  type RoomState,
  type RoomGrants,
  type RoomDriver,
  type RoomSlotUpdate,
  type RoomDriverLive,
  type RoomMyStage,
  type RoomStageLive,
  type RoomStageMember,
} from './roomProtocol'
import { isPersonKey, newPersonKey, type RoomActivity, type RoomResults } from './roomActivity'
import type { GameView } from './roomGame'

/** An activity as this device holds it: when it ends on this device's own clock (Date.now), or null for no limit. */
export type HeldActivity = RoomActivity & {
  /** When it ends, on this device's clock; null for no limit. */
  endsAt: number | null
  /** When it started, on this device's clock; null when the room did not say. */
  startedAt: number | null
}

/**
 * The room's messages about activities and people: the heat map, pins,
 * tally and raised hands, and (AYNI, 2026-10-12) the people, the meeting's
 * log and an activity's answers, for the presenter and moderators; each
 * participant's own answers; a poll's tally, for everyone.
 */
export type ActivityData = Extract<RoomServerMessage, { t: 'looks' | 'pins' | 'tally' | 'hands' | 'people' | 'log' | 'logRows' | 'mine' }>
import { logger } from '../utils/logger'

export interface RoomStatus {
  code: string
  connected: boolean
  /** Null until the room has said who leads. */
  role: 'lead' | 'follower' | null
  /** People in the room, this device included. */
  count: number
  /** Whether the room is a meeting: only a presenter leads. */
  meeting: boolean
  /** This device's seat in a meeting; null in an open room or before the room has answered. */
  seat: RoomSeat | null
  /** Whether anyone leads right now. False in a meeting whose presenter has not arrived. */
  hasLead: boolean
  /** The presenter's live media revision in a meeting; null when not live. A new number means pull again. */
  mediaRev: number | null
  /** This connection's media ticket in a meeting (see the welcome message); null otherwise. */
  ticket: string | null
  /** The activity under way (`roomActivity.ts`), or null. */
  activity: HeldActivity | null
  /** A revealed question's results, while they are up. */
  results: RoomResults | null
  /** The presenter has handed the globe to someone: whether to this device, their name, and whether the presenter has taken over for a moment. */
  spotlight: { me: boolean; name: string | null; override: boolean } | null
  /** AYNI: what the meeting lends this connection (the data tools, Education); null otherwise or once gone. */
  grants: RoomGrants | null
  /** AYNI (multi-globe plan): who drives which globe in the meeting; empty when nobody does. */
  drivers: RoomDriver[]
  /** AYNI (multi-globe plan): the globe this device drives (its position), or null. */
  driving: number | null
  /** AYNI (multi-globe plan): the drivers' live microphones, by globe, each as a revision. */
  driversLive: RoomDriverLive[]
  /**
   * AYNI (meeting games plan): the game as this connection may see it, and
   * when it arrived on this device's clock (its times count from there);
   * null when there is none.
   */
  game: GameView | null
  gameAt: number
  /** AYNI (owner, 2026-10-12): the main globe's driver's live microphone, as a revision; null when off. */
  spotLive: number | null
  /** AYNI: this person in a meeting — their number ("Guest 3") and the name the room has for them. */
  me: { n: number; name: string | null } | null
  /** AYNI: the presenter or a moderator removed this person from the meeting; the page does not reconnect. */
  kicked: boolean
  /** AYNI (owner, 2026-10-12): the presenter ended the meeting; the page does not reconnect. */
  ended: boolean
  /** AYNI (stage plan, owner 2026-11-03): who is on the meeting stage besides the presenter, and their live media, each as a revision. */
  stage: RoomStageMember[]
  stageLive: RoomStageLive[]
  /** AYNI (stage plan): this person's place on the stage (invited or on it), or null. */
  myStage: RoomMyStage | null
  /**
   * AYNI (stage plan): the last thing the room said about this person's
   * place — taken off by the presenter or a moderator, or their microphone
   * turned off — with a counter so the same notice twice is seen twice.
   */
  stageNotice: { why: 'removed' | 'muted'; n: number } | null
}

/** A fresh status: not connected, nothing known. */
export function emptyRoomStatus(code: string): RoomStatus {
  return {
    code, connected: false, role: null, count: 0, meeting: false, seat: null, hasLead: false, mediaRev: null, ticket: null, activity: null, results: null, spotlight: null, grants: null, drivers: [], driving: null, driversLive: [], game: null, gameAt: 0, spotLive: null, me: null, kicked: false, ended: false,
    stage: [], stageLive: [], myStage: null, stageNotice: null,
  }
}

/** How long a meeting's person key is kept unused before it is cleared from this browser. */
const PERSON_KEY_KEEP_MS = 3 * 24 * 60 * 60 * 1000

/**
 * AYNI (owner, 2026-10-12): this browser's key for meeting `code`, made on
 * first use and kept in its storage (every tab of the browser is the same
 * person; a key unused for three days is cleared). Without storage, one for
 * this page only. AYNI (S-10, 2026-10-21): used only against the rooms
 * Worker's own development handler (`VITE_ROOM_WS_URL`); the site's door
 * ignores it and issues the key itself (`claimRoomTicket`).
 */
export function personKeyFor(code: string, store: Storage | null = localStorageOrNull(), now = Date.now()): string {
  const name = `ayni-room-person:${code}`
  try {
    if (store) {
      for (let i = store.length - 1; i >= 0; i--) {
        const k = store.key(i)
        if (!k?.startsWith('ayni-room-person:') || k === name) continue
        const at = (JSON.parse(store.getItem(k) ?? '{}') as { at?: number }).at ?? 0
        if (now - at > PERSON_KEY_KEEP_MS) store.removeItem(k)
      }
      const kept = JSON.parse(store.getItem(name) ?? 'null') as { k?: unknown } | null
      const key = isPersonKey(kept?.k) ? kept!.k as string : newPersonKey()
      store.setItem(name, JSON.stringify({ k: key, at: now }))
      return key
    }
  } catch {
    // Storage refused or garbled: a key for this page only.
  }
  return newPersonKey()
}

function localStorageOrNull(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/**
 * AYNI (security fix PR 4, S-10): ask the site for this browser's ticket
 * cookie for the room before connecting, so the socket's upgrade carries it
 * and the door knows the person. Never fails the connection: without it the
 * door mints a ticket on the upgrade itself.
 */
export async function claimRoomTicket(code: string, fetchFn: typeof fetch = fetch): Promise<void> {
  try {
    await fetchFn(`/api/room/${encodeURIComponent(code)}/ticket`, { method: 'GET', credentials: 'same-origin', cache: 'no-store' })
  } catch {
    // Offline, or an older deployment without the route: the door's own cookie stands in.
  }
}

export interface RoomClientOptions {
  /** The lead's state arrived (never called on the lead itself). */
  onState: (state: RoomState) => void
  /** AYNI (multi-globe plan): a driver moved its globe (never called on that driver itself). */
  onSlot?: (n: number, update: RoomSlotUpdate) => void
  /** Connection, role or head-count changed. */
  onStatus: (status: RoomStatus) => void
  /** The heat map, pins and tally the room sends the lead and the moderator. */
  onActivityData?: (data: ActivityData) => void
  /** Where the room is; defaults to this site's own route. */
  url?: string
  /** The signed link's token (`?st=`), which names this device's seat in a meeting. */
  token?: string | null
  /**
   * AYNI (owner, 2026-10-12): this person's key in the address (`?pk=`).
   * AYNI (S-10): by default none — the site's door issues the key from its
   * cookie — except for a development server pointed straight at the rooms
   * Worker (`VITE_ROOM_WS_URL`), where it is `personKeyFor` the room unless
   * a token names the seat. Null sends none.
   */
  personKey?: string | null
  /**
   * AYNI (S-10): what to call before each connection so the browser holds
   * the room's ticket cookie; by default `claimRoomTicket` for the site's
   * own route, nothing for an explicit `url` or a development Worker. Null
   * calls nothing.
   */
  claimTicket?: ((code: string) => Promise<void>) | null
  /** Socket constructor, for tests. */
  createSocket?: (url: string) => WebSocket
}

export interface RoomClientHandle {
  status(): RoomStatus
  /** Send the lead's state. Ignored while not the lead or not connected. */
  send(state: RoomState): void
  /** Send an activity message (anything but a state). Ignored while not connected. */
  sendMessage(message: Exclude<RoomClientMessage, { t: 'state' }>): void
  close(): void
}

/** The room this page joined at boot, if any (see `joinedRoomCode`). */
let joinedCode: string | null = null

/**
 * The code of the room this page is in, or null. Read by the AR session
 * to decide whether to offer the marker scan: the address bar is not a
 * reliable place to look once the app has rewritten it for a dataset.
 */
export function joinedRoomCode(): string | null {
  return joinedCode
}

/** The room code in a page address (`?room=`), or null. */
export function roomCodeFromSearch(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): string | null {
  return normalizeRoomCode(new URLSearchParams(search).get('room'))
}

/** The signed-link token in a page address (`?st=`), or null. */
export function roomTokenFromSearch(
  search: string = typeof window === 'undefined' ? '' : window.location.search,
): string | null {
  const token = new URLSearchParams(search).get('st')?.trim()
  return token && token.length <= 400 ? token : null
}

/**
 * The signed link's token for room `code`, taken out of the address.
 *
 * A presenter's or moderator's address carries the token that is their
 * seat, and an address is the thing people copy and paste: a presenter
 * sharing "this page" would hand out the presenter's seat. So the token
 * is moved into this tab's own storage, where a reload still finds it,
 * and the address keeps only the room. Returns the token (from the
 * address, else from earlier in this tab), and the address without it,
 * or null when the address had none to remove.
 */
export function takeRoomToken(
  code: string,
  href: string = window.location.href,
  store: Storage | null = sessionStorageOrNull(),
): { token: string | null; cleanHref: string | null } {
  const key = `ayni-room-token:${code}`
  const url = new URL(href)
  const fromAddress = roomTokenFromSearch(url.search)
  if (fromAddress) {
    try {
      store?.setItem(key, fromAddress)
    } catch {
      // Storage refused: the token lasts as long as this page.
    }
    url.searchParams.delete('st')
    return { token: fromAddress, cleanHref: url.toString() }
  }
  let kept: string | null = null
  try {
    kept = store?.getItem(key) ?? null
  } catch {
    kept = null
  }
  return { token: kept, cleanHref: null }
}

function sessionStorageOrNull(): Storage | null {
  try {
    return typeof sessionStorage === 'undefined' ? null : sessionStorage
  } catch {
    return null
  }
}

/** The room's WebSocket address for a code, carrying the signed link's token when there is one. */
export function roomSocketUrl(
  code: string,
  override: string | undefined = import.meta.env.VITE_ROOM_WS_URL as string | undefined,
  location: { protocol: string; host: string } = window.location,
  token: string | null = null,
  personKey: string | null = null,
): string {
  const base = override?.trim()
  const params = [token ? `st=${encodeURIComponent(token)}` : '', personKey ? `pk=${encodeURIComponent(personKey)}` : ''].filter(Boolean)
  const query = params.length ? `?${params.join('&')}` : ''
  if (base) return `${base.endsWith('/') ? base : `${base}/`}${code}${query}`
  return `${location.protocol === 'https:' ? 'wss:' : 'ws:'}//${location.host}/api/room/${code}${query}`
}

/** First retry after this long, doubling up to the cap. */
const RETRY_FIRST_MS = 1000
const RETRY_MAX_MS = 15000

export function connectRoom(code: string, opts: RoomClientOptions): RoomClientHandle {
  joinedCode = code
  // AYNI (S-10): the site's door issues the person key; only the rooms Worker's development handler takes one from the address.
  const devWorker = !!(import.meta.env.VITE_ROOM_WS_URL as string | undefined)?.trim()
  const personKey = opts.personKey === undefined ? (opts.token || !devWorker ? null : personKeyFor(code)) : opts.personKey
  const url = opts.url ?? roomSocketUrl(code, undefined, undefined, opts.token ?? null, personKey)
  // A caller that brings its own socket (the output window, tests) has no door to claim a ticket from.
  const claimTicket = opts.claimTicket === undefined ? (opts.url === undefined && !devWorker && !opts.createSocket ? claimRoomTicket : null) : opts.claimTicket
  const createSocket = opts.createSocket ?? ((u: string) => new WebSocket(u))
  let socket: WebSocket | null = null
  let closed = false
  let retryMs = RETRY_FIRST_MS
  let retryTimer: ReturnType<typeof setTimeout> | null = null
  let myId: string | null = null
  const status: RoomStatus = emptyRoomStatus(code)
  const hold = (a: RoomActivity | null): HeldActivity | null =>
    a ? { ...a, endsAt: a.remainingMs === null ? null : Date.now() + a.remainingMs, startedAt: a.elapsedMs === null ? null : Date.now() - a.elapsedMs } : null

  const publish = (): void => opts.onStatus({ ...status })
  /** The connection the presenter handed the main globe to (its own id when it is this one), from the roster. */
  let spotId: string | null = null
  const setRoster = (lead: string | null, count: number, meeting: boolean, spotlight: { name: string | null; override?: boolean } | null = null, drivers: RoomDriver[] = []): void => {
    // While the presenter has taken over, the holder is not the lead: remember who holds it.
    if (spotlight && !spotlight.override) spotId = lead
    if (!spotlight) spotId = null
    status.spotlight = spotlight ? { me: spotId !== null && spotId === myId, name: spotlight.name, override: spotlight.override === true } : null
    status.role = myId === null || lead === null ? null : lead === myId ? 'lead' : 'follower'
    status.count = count
    status.meeting = meeting
    status.hasLead = lead !== null
    // AYNI (multi-globe plan): the drivers, and whether this device is one.
    status.drivers = drivers
    status.driving = drivers.find((d) => d.id === myId)?.slot ?? null
    // A room that stopped being a meeting has no seats.
    if (!meeting) status.seat = null
    publish()
  }

  function open(): void {
    if (closed) return
    let ws: WebSocket
    try {
      ws = createSocket(url)
    } catch (err) {
      logger.warn('[Room] could not open the socket:', err)
      scheduleRetry()
      return
    }
    socket = ws
    // Connected once the room has welcomed this page, not when the socket
    // opens: until then the status still holds what the last connection
    // left (a meeting, no activity), and a page told "connected" by it
    // would act on that.
    ws.addEventListener('open', () => {
      retryMs = RETRY_FIRST_MS
    })
    ws.addEventListener('message', (ev: MessageEvent) => {
      const msg = parseServerMessage(ev.data)
      if (!msg) return
      if (msg.t === 'welcome') {
        status.connected = true
        myId = msg.you
        status.seat = msg.seat
        status.mediaRev = msg.media
        status.ticket = msg.ticket
        status.activity = hold(msg.activity)
        status.results = msg.results
        status.grants = msg.grants
        status.driversLive = msg.driversLive
        status.spotLive = msg.spotLive
        status.me = msg.me
        status.stage = msg.stage
        status.stageLive = msg.stageLive
        status.myStage = msg.myStage
        setRoster(msg.lead, msg.count, msg.meeting, msg.spotlight, msg.drivers)
        if (msg.state && status.role === 'follower') opts.onState(msg.state)
        // The driven globes' last cameras, for someone arriving mid-session.
        for (const d of msg.driven) if (status.driving !== d.n) opts.onSlot?.(d.n, d.s)
      } else if (msg.t === 'roster') {
        status.stage = msg.stage
        setRoster(msg.lead, msg.count, msg.meeting, msg.spotlight, msg.drivers)
      } else if (msg.t === 'slot') {
        if (status.driving !== msg.n) opts.onSlot?.(msg.n, msg.s)
      } else if (msg.t === 'media') {
        status.mediaRev = msg.rev
        status.driversLive = msg.drivers ?? []
        status.spotLive = msg.spot ?? null
        status.stageLive = msg.stage ?? []
        publish()
      } else if (msg.t === 'stage') {
        // AYNI (stage plan): this person's place on the stage changed.
        status.myStage = msg.me
        if (msg.why) status.stageNotice = { why: msg.why, n: (status.stageNotice?.n ?? 0) + 1 }
        publish()
      } else if (msg.t === 'ended') {
        // The presenter ended the meeting: say so, and stay out.
        logger.warn('[Room] the meeting has ended')
        closed = true
        status.ended = true
        publish()
      } else if (msg.t === 'kicked') {
        // Removed from the meeting: say so, and stay out (the room refuses this person's key anyway).
        logger.warn('[Room] removed from the meeting')
        closed = true
        status.kicked = true
        publish()
      } else if (msg.t === 'activity') {
        status.activity = hold(msg.a)
        // An ended activity takes its results down with it.
        if (!msg.a) status.results = null
        publish()
      } else if (msg.t === 'results') {
        status.results = msg.r
        publish()
      } else if (msg.t === 'game') {
        status.game = msg.v
        status.gameAt = Date.now()
        publish()
      } else if (msg.t === 'looks' || msg.t === 'pins' || msg.t === 'tally' || msg.t === 'hands' || msg.t === 'people' || msg.t === 'log' || msg.t === 'logRows' || msg.t === 'mine') {
        opts.onActivityData?.(msg)
      } else if (status.role !== 'lead') {
        opts.onState(msg.s)
      }
    })
    const onGone = (ev?: Event): void => {
      if (socket !== ws) return
      // Said once per drop, so a class whose connections keep dropping shows it in the console.
      const close = ev as CloseEvent | undefined
      logger.warn(`[Room] connection lost (${close?.code ?? 'error'}${close?.reason ? `: ${close.reason}` : ''}); reconnecting`)
      socket = null
      myId = null
      status.connected = false
      status.role = null
      status.hasLead = false
      status.mediaRev = null
      status.ticket = null
      status.activity = null
      status.results = null
      status.grants = null
      status.spotlight = null
      status.drivers = []
      status.driving = null
      status.driversLive = []
      status.game = null
      status.spotLive = null
      status.stage = []
      status.stageLive = []
      status.myStage = null
      publish()
      scheduleRetry()
    }
    ws.addEventListener('close', onGone)
    ws.addEventListener('error', () => {
      try { ws.close() } catch { /* already closing */ }
    })
  }

  /** The ticket cookie first (S-10), then the socket; `open` checks `closed` again after the wait. */
  function openWithTicket(): void {
    if (closed) return
    if (!claimTicket) {
      open()
      return
    }
    void claimTicket(code).then(() => open(), () => open())
  }

  function scheduleRetry(): void {
    if (closed || retryTimer !== null) return
    retryTimer = setTimeout(() => {
      retryTimer = null
      openWithTicket()
    }, retryMs)
    retryMs = Math.min(RETRY_MAX_MS, retryMs * 2)
  }

  openWithTicket()

  return {
    status: () => ({ ...status }),
    send(state) {
      if (status.role !== 'lead' || !socket || socket.readyState !== 1) return
      const message: RoomClientMessage = { t: 'state', s: state }
      try {
        socket.send(JSON.stringify(message))
      } catch {
        // Closing; the close handler reconnects.
      }
    },
    sendMessage(message) {
      if (!socket || socket.readyState !== 1) return
      try {
        socket.send(JSON.stringify(message))
      } catch {
        // Closing; the close handler reconnects.
      }
    },
    close() {
      closed = true
      if (joinedCode === code) joinedCode = null
      if (retryTimer !== null) clearTimeout(retryTimer)
      retryTimer = null
      try { socket?.close() } catch { /* already closed */ }
      socket = null
    },
  }
}
